import experimentService, { IVariants } from "@/common/services/experiment/experimentService";
import { getDeviceType, getWindowWidth } from "@/common/utils/device";
import { apiMetrics } from "@/common/services";
import { EXPERIMENT_NO_ADS, EXPERIMENT_USE_CUSTOM_HLS } from "@/common/services/experiment/constants";

const loader = () => import("web-vitals");

const ENABLED_COUNTRIES = ["US", "UK", "DE"];

const ENABLED_ENVIRONMENTS = ["production", "staging"];

const WHITE_LIST_EXPERIMENTS = [EXPERIMENT_NO_ADS, EXPERIMENT_USE_CUSTOM_HLS];

const WHITE_LIST_PAGE_GROUPS = [
  "home", "ifr", "search", "users", "watch", "create", "auth",
  "niches", "explore", "categories", "settings", "dashboard",
  "followers", "dmca", "creators", "faq", "following", "user",
  "guidelines", "profile", "privacy", "unsubscribed", "signup",
  "notice2257", "terms", "search-warning", "authorize", "app",
];

declare global {
  interface Window {
    geo_country: string;
  }
}

interface IMetric {
  name: string;
  delta: number;
}

interface ISendMetricsParams {
  pageGroup: string;
  deviceType: string;
  experiments: string[];
}

type IMetricsData = {
  lcp?: number;
  fcp?: number;
  cls?: number;
  inp?: number;
  ttfb?: number;
}

const defaultMetricsData: IMetricsData = {
  lcp: undefined,
  fcp: undefined,
  cls: undefined,
  inp: undefined,
  ttfb: undefined,
};

let metricsData: IMetricsData = { ...defaultMetricsData };

const getPageGroup = (pathname: string) => {
  if (pathname === "/") {
    return "home";
  }

  return pathname.split("/")[1];
};

const getExperiments = (variants: IVariants): string[] => {
  return Object.keys(variants)
    .filter((key: string) => {
      if (!WHITE_LIST_EXPERIMENTS.includes(key)) {
        return false;
      }

      if (!variants[key]) {
        return false;
      }

      return variants[key] !== "off";
    });
};

const initialize = async () => {
  if (!import.meta.env.REACT_APP_WEB_VITALS_SAMPLE_RATE) {
    return;
  }

  if (Math.random() > Number(import.meta.env.REACT_APP_WEB_VITALS_SAMPLE_RATE)) {
    return;
  }

  const pageGroup = getPageGroup(window.location.pathname);

  if (!WHITE_LIST_PAGE_GROUPS.includes(pageGroup)) {
    return;
  }

  if (!ENABLED_ENVIRONMENTS.includes(import.meta.env.MODE)) {
    return;
  }

  const experimentVariants = await experimentService.getAllVariants();

  const country = window.geo_country ?? null;

  if (import.meta.env.MODE === "production" && !ENABLED_COUNTRIES.includes(country)) {
    return;
  }

  const { onLCP, onCLS, onINP, onTTFB, onFCP } = await loader();

  const params = {
    pageGroup,
    deviceType: getDeviceType(getWindowWidth()),
    experiments: getExperiments(experimentVariants),
  };

  onCLS(saveMetric);
  onLCP(saveMetric);
  onINP(saveMetric);
  onTTFB(saveMetric);
  onFCP(saveMetric);

  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      sendMetrics(metricsData, params);
      metricsData = { ...defaultMetricsData };
    }
  });

  console.debug("Web Vitals initialized.");
};

const saveMetric = (metric: IMetric) => {
  const name = (metric.name.toLowerCase()) as keyof IMetricsData;
  metricsData[name] = metric.delta;
};

const sendMetrics = (metrics: IMetricsData, params: ISendMetricsParams) => {
  const validMetrics = filterMetrics(metrics);

  if (Object.keys(validMetrics).length === 0) {
    return;
  }

  const payload = {
    ...metrics,
    page_group: params.pageGroup,
    device_type: params.deviceType,
    experiments: params.experiments,
  };

  return apiMetrics.sendWebVitalsMetrics(payload);
};

const filterMetrics = (metrics: IMetricsData) => {
  const validMetrics: Partial<IMetricsData> = {};

  for (const prop in metrics) {
    const key = prop as keyof IMetricsData;
    const value = metrics[key];

    if (value !== undefined && value >= 0) {
      validMetrics[key] = metrics[key];
    }
  }

  return validMetrics;
};

export const startWebVitals = () => {
  try {
    initialize();
  } catch (e) {
    console.error(`Error initializing Web Vitals: ${e}`);
  }
};
